import { useState } from 'react';

// Material-UI
import { styled } from '@mui/material/styles';
import {
    Box,
    Grid,
    Typography,
    Switch,
    InputAdornment,
    FormControlLabel,
    CircularProgress,
} from '@mui/material';
import { Alert } from '@mui/material';

// Components
import { TextField, ProvinceSelector, DatePicker, TimePicker } from 'components/ui/inputs';
import AddressAutocomplete from 'components/ui/AddressAutocomplete';
import { Section, Header, Content } from 'components/ui/forms';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';
import ImagePicker from 'components/ImagePicker';
import FormSubmitBar from 'components/forms/FormSubmitBar';
import ConfirmAction from 'components/popups/ConfirmAction';
import SeasonFields from 'components/forms/SeasonFields';

// Utility
import useExpresso from 'hooks/useExpresso';
import { validatePostalCode, getMomentFromDateAndTime, isRichTextEmpty } from 'utility/validation';
import moment from 'moment-timezone';
import { useNotification } from 'context/notification';
import { CA_TIMEZONES, US_TIMEZONES } from 'utility/dates';
import { normalizeCountryCode } from 'utility/forms';
import { RichEditor } from 'components/RichEditor';
//import { useTextInput, useNumberInput, useToggle, useDatePicker } from 'hooks/forms';

// TODO: Refactor this form to use form hooks
//       Starter code is below

// const type          = useTextInput ({ defaultValue: defaultValues?.type         ?? ''                               });
// const title         = useTextInput ({ defaultValue: defaultValues?.title        ?? ''                               });
// const description   = useTextInput ({ defaultValue: defaultValues?.description  ?? ''                               });
// const venueName     = useTextInput ({ defaultValue: defaultValues?.venueName    ?? ''                               });
// const venueAddress  = useTextInput ({ defaultValue: defaultValues?.venueAddress ?? ''                               });
// const city          = useTextInput ({ defaultValue: defaultValues?.city         ?? ''                               });
// const province      = useTextInput ({ defaultValue: defaultValues?.province     ?? ''                               });
// const postalCode    = useTextInput ({ defaultValue: defaultValues?.postalCode   ?? ''                               });
// const country       = useTextInput ({ defaultValue: defaultValues?.country      ?? 'Canada'                         });
// const timezone      = useTextInput ({ defaultValue: defaultValues?.timezone     ?? ''                               });
// const startDate     = useDatePicker({ defaultValue: defaultValues?.startDate    ?? moment({ hour: 12, minute: 0 })  });
// const startTime     = useDatePicker({ defaultValue: defaultValues?.startDate    ?? moment({ hour: 12, minute: 0 })  });
// const endDate       = useDatePicker({ defaultValue: defaultValues?.endDate      ?? moment({ hour: 23, minute: 45 }) });
// const endTime       = useDatePicker({ defaultValue: defaultValues?.endDate      ?? moment({ hour: 23, minute: 45 }) });
// const taxName       = useTextInput ({ defaultValue: defaultValues?.taxName      ?? ''                               });
// const taxRate       = useTextInput ({ defaultValue: defaultValues?.taxRate      ?? ''                               });
// const posTaxRate    = useTextInput ({ defaultValue: defaultValues?.posTaxRate   ?? ''                               });

// const collectTax    = useToggle    ({ defaultValue: Boolean(defaultValues?.taxName && defaultValues?.taxRate)       });
// const collectPosTax = useToggle    ({ defaultValue: Boolean(defaultValues?.taxName && defaultValues?.posTaxRate)    });

function getTimezonesForCountry(countryValue) {
    const countryCode = normalizeCountryCode(countryValue);

    if (countryCode === 'CA') return CA_TIMEZONES;
    if (countryCode === 'US') return US_TIMEZONES;

    return US_TIMEZONES;
}

/**
 * This function returns the state object containing all of the default values to use
 * Used when the component renders, and when the user wants to undo their changes
 *
 * NOTE: image, collectTax flags are not set in this function
 */

function initializeFormState(defaultValues) {
    const defaultState = {
        parentEventId: { value: defaultValues?.parentEventId ?? '', error: false, message: '' },
        seasonAdmissionCapacity: {
            value: defaultValues?.seasonAdmissionCapacity ?? '',
            error: false,
            message: '',
        },
        type: { value: defaultValues?.type ?? '', error: false, message: '' },
        title: { value: defaultValues?.title ?? '', error: false, message: '' },
        description: { value: defaultValues?.description ?? '', error: false, message: '' },
        htmlDescription: {
            value:
                defaultValues?.eventDescHtml ||
                (defaultValues?.description
                    ? defaultValues.description.replace(/\n/g, '<br>')
                    : ''),
            error: false,
            message: '',
        },
        venueName: { value: defaultValues?.venueName ?? '', error: false, message: '' },
        venueAddress: { value: defaultValues?.venueAddress ?? '', error: false, message: '' },
        city: { value: defaultValues?.city ?? '', error: false, message: '' },
        province: { value: defaultValues?.province ?? '', error: false, message: '' },
        postalCode: { value: defaultValues?.postalCode ?? '', error: false, message: '' },
        country: { value: defaultValues?.country ?? '', error: false, message: '' },
        timezone: { value: defaultValues?.timezone ?? '', error: false, message: '' },
        startDate: {
            value: defaultValues?.startDate ?? moment({ hour: 12, minute: 0 }),
            error: false,
            message: '',
        },
        startTime: {
            value: defaultValues?.startDate ?? moment({ hour: 12, minute: 0 }),
            error: false,
            message: '',
        },
        endDate: {
            value: defaultValues?.endDate ?? moment({ hour: 23, minute: 45 }),
            error: false,
            message: '',
        },
        endTime: {
            value: defaultValues?.endDate ?? moment({ hour: 23, minute: 45 }),
            error: false,
            message: '',
        },
        seatingChartKey: { value: defaultValues?.seatingChartKey ?? '', error: false, message: '' },
        taxName: { value: defaultValues?.taxName ?? '', error: false, message: '' },
        taxRate: { value: defaultValues?.taxRate ?? '', error: false, message: '' },
        posTaxRate: { value: defaultValues?.posTaxRate ?? '', error: false, message: '' },
    };

    return defaultState;
}

/**
 * Base form component
 * Can be used for creating events, or editing existing ones
 * Handles all form logic including validation
 */

const EMPTY_OPTIONS = [];

export default function EventForm(props) {
    // Props
    const {
        defaultValues,
        onSubmit,
        onCancel,
        loading,
        editMode,
        readOnly,
        hostSeatsEnabled = false,
        seatingCharts = EMPTY_OPTIONS,
        seasons = EMPTY_OPTIONS,
        onFormChange,
        onFormReset,
        unsavedChanges,
        showTaxWarning = false,
        childCount = 0,
    } = props;

    // State
    const [image, setImage] = useState(defaultValues?.image ?? null);

    const [values, setValues] = useState(() => initializeFormState(defaultValues));

    const [collectTax, setCollectTax] = useState(
        Boolean(defaultValues?.taxName && defaultValues?.taxRate),
    );
    const [collectPosTax, setCollectPosTax] = useState(Boolean(defaultValues?.posTaxRate));

    const [assignedSeatingEnabled, setAssignedSeatingEnabled] = useState(
        Boolean(defaultValues?.seatingChartKey),
    );
    const [showTaxConfirmDialog, setShowTaxConfirmDialog] = useState(false);

    const {
        type,
        title,
        htmlDescription,
        venueName,
        venueAddress,
        city,
        province,
        postalCode,
        country,
        startDate,
        startTime,
        endDate,
        endTime,
        seatingChartKey,
        timezone,
        taxName,
        taxRate,
        posTaxRate,
    } = values;

    // Hooks
    const [eventTypes] = useExpresso('/api/event_types'); // TODO: Fetch this data from parent components
    const { notify } = useNotification();

    let rangeError = false;

    const hasSeatingCharts = seatingCharts.length > 0;
    const parentMode = Number(defaultValues?.parentMode) === 1;
    const seasonLinked = Boolean(values.parentEventId.value);
    const selectedSeason = seasons.find(
        (season) => String(season.event_id) === String(values.parentEventId.value),
    );
    const inheritedSeating =
        seasonLinked &&
        (Number(selectedSeason?.seats_flag) === 1 ||
            (!selectedSeason &&
                Boolean(defaultValues?.parentEventId && defaultValues?.seatingChartKey)));
    const relationshipLocked = Boolean(
        editMode && defaultValues?.parentEventId && defaultValues?.seatingChartKey,
    );
    const seatsClosed = Boolean(defaultValues?.seatsClosedAt);
    const disableSeatingChart =
        seatsClosed ||
        (editMode &&
            parentMode &&
            ((childCount > 0 && Boolean(defaultValues?.seatingChartKey)) ||
                Number(defaultValues?.orderCount) > 0 ||
                Number(defaultValues?.posOrderCount) > 0));
    const dateReadOnly = readOnly || seatsClosed;
    const seasonFieldChanged =
        String(values.parentEventId.value || '') !== String(defaultValues?.parentEventId || '') ||
        String(values.seasonAdmissionCapacity.value) !==
            String(defaultValues?.seasonAdmissionCapacity ?? '');
    const entityLabel = parentMode ? 'Season' : 'Event';
    const timezoneOptions = getTimezonesForCountry(country.value);

    const seatingChartThumbnail = seatingCharts.find(
        (s) => s.key === seatingChartKey.value,
    )?.thumbnail;

    // Check if end date comes after start date
    if (
        !values.startDate.error &&
        !values.endDate.error &&
        !values.startTime.error &&
        !values.endTime.error
    ) {
        const fullStartDate = getMomentFromDateAndTime(
            values.startDate.value,
            values.startTime.value,
        );
        const fullEndDate = getMomentFromDateAndTime(values.endDate.value, values.endTime.value);

        if (fullEndDate < fullStartDate) {
            rangeError = true;
        }
    }

    function handleFormReset() {
        setValues(initializeFormState(defaultValues));
        setImage(defaultValues?.image ?? null);
        setCollectTax(Boolean(defaultValues?.taxName && defaultValues?.taxRate));
        setCollectPosTax(Boolean(defaultValues?.posTaxRate));
        setAssignedSeatingEnabled(Boolean(defaultValues?.seatingChartKey));
        onFormReset(); // Turns off unsaved changes flag in parent component
    }

    function handleFormChange(e) {
        if (onFormChange) onFormChange();

        const { id, value } = e.target;

        switch (id) {
            case 'parentEventId':
                setValues({
                    ...values,
                    parentEventId: { value, error: false, message: '' },
                    seasonAdmissionCapacity: {
                        ...values.seasonAdmissionCapacity,
                        error: false,
                        message: '',
                    },
                });
                break;
            case 'htmlDescription':
                setValues({
                    ...values,
                    description: { value: value.text, error: false, message: '' }, // unformatted text (used in some metadata)
                    htmlDescription: { value: value.html, error: false, message: '' }, // formatted text (for the customer to see)
                });
                break;
            case 'country':
                if (seatsClosed) return;
                setValues({
                    ...values,
                    country: { value, error: false, message: '' },
                    province: { value: '', error: false, message: '' },
                    postalCode: { value: '', error: false, message: '' },
                    timezone: { value: '', error: false, message: '' },
                });
                break;

            case 'postalCode':
                const newValue =
                    values.country.value === 'Canada'
                        ? value
                              .replace(/[^0-9a-zA-Z]/g, '')
                              .slice(0, 6)
                              .toUpperCase()
                        : value.replace(/[^0-9]/g, '').slice(0, 9);
                setValues({
                    ...values,
                    postalCode: { value: newValue, error: false, message: '' },
                });
                break;

            case 'timezone':
                setValues({
                    ...values,
                    timezone: { ...timezone, value, error: false, message: '' },
                });
                break;

            default:
                setValues({ ...values, [id]: { value, error: false, message: '' } });
        }
    }

    function handleImageChange(value) {
        if (onFormChange) onFormChange();
        setImage(value);
    }

    function handleAddressAutocomplete({ address, city, province, country, postalCode }) {
        if (onFormChange) onFormChange();

        setValues((prev) => ({
            ...prev,
            venueAddress: { value: address, error: false, message: '' },
            city: { value: city, error: false, message: '' },
            province: { value: province, error: false, message: '' },
            country: { value: country, error: false, message: '' },
            postalCode: { value: postalCode, error: false, message: '' },
            timezone:
                !seatsClosed && prev.country.value !== country // Keep the timezone of a closed event
                    ? { value: '', error: false, message: '' }
                    : prev.timezone,
        }));
    }

    function handleSwitchChange(e) {
        if (onFormChange) onFormChange();

        const { id, checked } = e.target;

        if (id === 'collectTax') {
            setCollectTax(checked);

            if (checked === false) {
                setValues({
                    ...values,
                    taxName: { ...values.taxName, error: false, message: '' },
                    taxRate: { ...values.taxRate, error: false, message: '' },
                });
            }
        }

        if (id === 'collectPosTax') {
            setCollectPosTax(checked);

            if (checked === false) {
                setValues({
                    ...values,
                    posTaxRate: { ...values.posTaxRate, error: false, message: '' },
                });
            }
        }
    }

    function handleAssignedSeatingToggle(e) {
        const { checked } = e.target;
        setAssignedSeatingEnabled(checked);

        if (checked) {
            if (seatingCharts.length === 1) {
                handleFormChange({
                    target: { id: 'seatingChartKey', value: String(seatingCharts[0].key) },
                });
            } else {
                handleFormChange({ target: { id: 'seatingChartKey', value: '' } });
            }
        } else {
            handleFormChange({ target: { id: 'seatingChartKey', value: '' } });
        }
    }

    function handlePercentChange(e) {
        if (onFormChange) onFormChange();

        let value = e.target.value.replace(/[^0-9.]/g, '');
        let [whole, decimal] = value.split('.');

        if (whole?.length > 8) {
            whole = whole.slice(0, 8);
        }

        if (decimal?.length > 4) {
            decimal = decimal.slice(0, 4);
        }

        let finalValue = whole;

        if (value.includes('.')) {
            finalValue += '.';
        }

        if (decimal) {
            finalValue += decimal;
        }

        if (e.target.id === 'taxRate') {
            setValues({ ...values, taxRate: { value: finalValue, error: false, message: '' } });
        }

        if (e.target.id === 'posTaxRate') {
            setValues({ ...values, posTaxRate: { value: finalValue, error: false, message: '' } });
        }
    }

    function closeTaxWarningModal() {
        setShowTaxConfirmDialog(false);
    }

    function handleSubmitWithTaxWarning() {
        const formData = {
            taxRate: collectTax ? values.taxRate.value : null,
            posTaxRate: collectPosTax ? values.posTaxRate.value : null,
        };

        const normalizeTaxRate = (value) => {
            if (value === null || value === undefined || value === '') {
                return null;
            }

            const numericValue = Number(value);
            return Number.isNaN(numericValue) ? null : numericValue;
        };

        const taxRatesChanged =
            normalizeTaxRate(formData.taxRate) !== normalizeTaxRate(defaultValues?.taxRate) ||
            normalizeTaxRate(formData.posTaxRate) !== normalizeTaxRate(defaultValues?.posTaxRate);

        if (!showTaxWarning || !taxRatesChanged) {
            handleSubmit();
            return;
        }

        setShowTaxConfirmDialog(true);
    }

    function handleDateChange(id, value) {
        if (onFormChange) onFormChange();

        if (id === 'startDate' && moment(values.endDate.value).isBefore(moment(value))) {
            setValues({
                ...values,
                startDate: { value, error: false, message: '' },
                endDate: { value, error: false, message: '' },
            });
        } else {
            setValues({ ...values, [id]: { value, error: false, message: '' } });
        }
    }

    function handleSubmit() {
        let newState = { ...values };

        const fieldsToValidate = [
            'type',
            'title',
            // description - we leave this out since its not for display purposes
            'htmlDescription',
            'venueName',
            'venueAddress',
            'city',
            'province',
            'postalCode',
            'timezone',
        ];

        if (collectTax) {
            fieldsToValidate.push('taxName', 'taxRate');
        } else {
            newState.taxName = { ...values.taxName, error: false, message: '' };
            newState.taxRate = { ...values.taxRate, error: false, message: '' };
        }

        if (collectPosTax) {
            fieldsToValidate.push('posTaxRate');
        } else {
            newState.posTaxRate = { ...values.posTaxRate, error: false, message: '' };
        }

        if (assignedSeatingEnabled && !seasonLinked) {
            fieldsToValidate.push('seatingChartKey');
        } else {
            newState.seatingChartKey = { ...values.seatingChartKey, error: false, message: '' };
        }

        // Check these values for empty strings
        fieldsToValidate.forEach((key) => {
            if (key === 'htmlDescription') {
                // Special validation for rich text editor
                if (isRichTextEmpty(values[key].value)) {
                    newState[key] = {
                        ...values[key],
                        error: true,
                        message: 'This field cannot be empty',
                    };
                } else {
                    newState[key] = { ...values[key] };
                }
            } else {
                String(values[key].value).trim() === ''
                    ? (newState[key] = {
                          ...values[key],
                          error: true,
                          message: 'This field cannot be empty',
                      })
                    : (newState[key] = { ...values[key] });
            }
        });

        if (seasonLinked && !inheritedSeating && (!editMode || seasonFieldChanged)) {
            const capacity = Number(values.seasonAdmissionCapacity.value);
            if (
                String(values.seasonAdmissionCapacity.value).trim() !== '' &&
                (!Number.isSafeInteger(capacity) || capacity <= 0)
            ) {
                newState.seasonAdmissionCapacity = {
                    ...values.seasonAdmissionCapacity,
                    error: true,
                    message: 'Enter a valid number or leave blank for no limit',
                };
            }
        }

        // Check if tax rate is between 0 and 30
        if (collectTax && newState.taxRate.error === false) {
            const taxRate = values.taxRate.value;

            if (taxRate <= 0 || taxRate > 30) {
                newState.taxRate = {
                    ...values.taxRate,
                    error: true,
                    message: 'Please enter a valid tax rate',
                };
            }
        }

        // Check if tax rate is between 0 and 30
        if (collectPosTax && newState.posTaxRate.error === false) {
            const posTaxRate = values.posTaxRate.value;

            if (posTaxRate <= 0 || posTaxRate > 30) {
                newState.posTaxRate = {
                    ...values.posTaxRate,
                    error: true,
                    message: 'Please enter a valid tax rate',
                };
            }
        }

        // Check for valid start and end dates
        ['startDate', 'endDate'].forEach((key) => {
            if (!moment(values[key].value).isValid()) {
                newState[key] = {
                    ...values[key],
                    error: true,
                    message: 'Please enter a valid date',
                };
            } else {
                newState[key] = { ...values[key] };
            }
        });

        // Check for valid start and end times
        ['startTime', 'endTime'].forEach((key) => {
            if (!moment(values[key].value).isValid()) {
                newState[key] = {
                    ...values[key],
                    error: true,
                    message: 'Please enter a valid time',
                };
            } else {
                newState[key] = { ...values[key] };
            }
        });

        // Check for valid postal code
        if (newState.postalCode.value !== '') {
            if (validatePostalCode(values.postalCode.value, values.country.value) === false) {
                newState.postalCode = {
                    ...values.postalCode,
                    error: true,
                    message: `Please enter a valid ${values.country.value === 'Canada' ? 'postal code' : 'ZIP code'}`,
                };
            } else {
                newState.postalCode = { ...values.postalCode, error: false, message: '' };
            }
        }

        // Check if end date comes after start date
        let startDateAfterEndDate = false;
        if (
            !newState.startDate.error &&
            !newState.endDate.error &&
            !newState.startTime.error &&
            !newState.endTime.error
        ) {
            const fullStartDate = getMomentFromDateAndTime(
                newState.startDate.value,
                newState.startTime.value,
            );
            const fullEndDate = getMomentFromDateAndTime(
                newState.endDate.value,
                newState.endTime.value,
            );

            if (fullEndDate < fullStartDate) {
                startDateAfterEndDate = true;
            }
        }

        // Return if any fields are invalid
        if (Object.keys(newState).some((key) => newState[key].error) || startDateAfterEndDate) {
            setValues(newState);
            notify.warning('Please make sure every field is filled in correctly');
            return null;
        }
        const startDate = getMomentFromDateAndTime(
            values.startDate.value,
            values.startTime.value,
        ).format('YYYY-MM-DD HH:mm:00');
        const endDate = getMomentFromDateAndTime(values.endDate.value, values.endTime.value).format(
            'YYYY-MM-DD HH:mm:59',
        );

        const formData = {
            eventTypeId: values.type.value,
            title: values.title.value,
            htmlDescription: values.htmlDescription.value,
            description: values.description.value,
            venue: values.venueName.value,
            address: values.venueAddress.value,
            city: values.city.value,
            province: values.province.value,
            postalCode: values.postalCode.value,
            country: values.country.value,
            startDate: startDate,
            endDate: endDate,
            seatingChartKey: assignedSeatingEnabled ? seatingChartKey.value : null,
            timezone: values.timezone.value,
            taxName: collectTax ? values.taxName.value : null,
            taxRate: collectTax ? values.taxRate.value : null,
            posTaxRate: collectPosTax ? values.posTaxRate.value : null,
        };

        // The server owns the inherited layout and Seats.io event key.
        if (seasonLinked) delete formData.seatingChartKey;
        if (seatsClosed) {
            delete formData.startDate;
            delete formData.endDate;
            delete formData.timezone;
            delete formData.seatingChartKey;
        }
        if (!editMode) formData.parentMode = parentMode ? 1 : 0;
        if (parentMode && assignedSeatingEnabled) formData.seasonAdmissionCapacity = null;
        if (!parentMode && (!editMode || seasonFieldChanged)) {
            formData.parentEventId = values.parentEventId.value
                ? Number(values.parentEventId.value)
                : null;
            formData.seasonAdmissionCapacity =
                seasonLinked &&
                !inheritedSeating &&
                String(values.seasonAdmissionCapacity.value).trim() !== ''
                    ? Number(values.seasonAdmissionCapacity.value)
                    : null;
        }

        // Edit Mode
        if (editMode) {
            if (image?.includes('https://')) {
                // Preview image hasn't changed. Don't update eventImage
            } else {
                formData.eventImage = image;
            }
            // Create Mode
        } else {
            formData.eventImage = image;
        }

        onSubmit(formData);
    }

    return (
        <Box display="flex" flexDirection="column" alignItems="center" width="100%">
            <ConfirmAction
                open={showTaxConfirmDialog}
                onConfirm={() => {
                    handleSubmit();
                    closeTaxWarningModal();
                }}
                onCancel={closeTaxWarningModal}
                title="Confirm Tax Change"
                description="This event has live orders. Changing taxes now may require extra review in your reporting and reconciliation. Please consult your accountant or treasurer first."
                confirmText="Proceed"
            />
            {readOnly === true && (
                <Box width="100%" mb={2}>
                    <Alert severity="info">This event has concluded and cannot be edited</Alert>
                </Box>
            )}

            <FormContainer>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <SeasonFields
                            parentMode={parentMode}
                            parentEventId={values.parentEventId.value}
                            capacity={values.seasonAdmissionCapacity.value}
                            capacityError={values.seasonAdmissionCapacity.message}
                            seasons={seasons}
                            disabled={readOnly || loading}
                            seatingEnabled={assignedSeatingEnabled && !inheritedSeating}
                            seatingChartKey={values.seatingChartKey.value}
                            seatingLayoutName={
                                seatingCharts.find(
                                    (chart) =>
                                        chart.key ===
                                        (selectedSeason?.seat_chart_key ||
                                            defaultValues?.seatingChartKey),
                                )?.name
                            }
                            inheritedSeating={inheritedSeating}
                            relationshipLocked={relationshipLocked}
                            onChange={handleFormChange}
                            used={defaultValues?.seasonAdmissionUsed}
                            remaining={defaultValues?.seasonAdmissionRemaining}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Section>
                            <Header>About</Header>
                            <Typography>
                                Provide essential details for your event. Set the <Bold>Title</Bold>{' '}
                                to grab purchasers attention. Use the <Bold>Description</Bold> to
                                share important information. Select a <Bold>Category</Bold> to help
                                purchasers discover your event easily.
                            </Typography>
                            <Content>
                                <TextField
                                    id="title"
                                    disabled={readOnly}
                                    value={title.value}
                                    error={title.error}
                                    helperText={title.message}
                                    onChange={handleFormChange}
                                    label={`${entityLabel} Title`}
                                />
                                <RichEditor
                                    label={`${entityLabel} Description`}
                                    value={htmlDescription.value}
                                    error={htmlDescription.error}
                                    helperText={htmlDescription.message}
                                    onChange={({ html, text }) => {
                                        handleFormChange({
                                            target: {
                                                id: 'htmlDescription',
                                                value: { html, text },
                                            },
                                        });
                                    }}
                                    placeholder={`Describe your ${entityLabel.toLowerCase()} in detail...`}
                                />
                                <TextField
                                    id="type"
                                    disabled={readOnly}
                                    value={type.value}
                                    error={type.error}
                                    helperText={type.message}
                                    onChange={handleFormChange}
                                    select
                                    InputLabelProps={{ shrink: true }}
                                    SelectProps={{ native: true }}
                                    label={`${entityLabel} Category`}
                                >
                                    <option disabled value=""></option>
                                    {eventTypes &&
                                        eventTypes.rows.map((e) => (
                                            <option key={e.event_type_id} value={e.event_type_id}>
                                                {e.type_name}
                                            </option>
                                        ))}
                                </TextField>
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>{entityLabel} Location</Header>
                            <Typography>
                                Enter the venue name and full address. This appears on the ticket
                                purchase page and on the tickets.
                            </Typography>
                            <Content>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            id="venueName"
                                            disabled={readOnly}
                                            value={venueName.value}
                                            error={venueName.error}
                                            helperText={venueName.message}
                                            onChange={handleFormChange}
                                            label="Venue Name"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <AddressAutocomplete
                                            id="venueAddress"
                                            disabled={readOnly}
                                            defaultValue={venueAddress.value}
                                            error={venueAddress.error}
                                            helperText={venueAddress.message}
                                            onChange={handleFormChange}
                                            onPlaceSelect={handleAddressAutocomplete}
                                            label="Venue Address"
                                            country={country.value}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            id="city"
                                            disabled={readOnly}
                                            value={city.value}
                                            error={city.error}
                                            helperText={city.message}
                                            onChange={handleFormChange}
                                            label="City"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <ProvinceSelector
                                            id="province"
                                            disabled={readOnly}
                                            region={country.value}
                                            value={province.value}
                                            error={province.error}
                                            helperText={province.message}
                                            onChange={handleFormChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            value={postalCode.value}
                                            disabled={readOnly}
                                            error={postalCode.error}
                                            helperText={postalCode.message}
                                            id="postalCode"
                                            onChange={handleFormChange}
                                            label={
                                                country.value === 'Canada' ? 'Postal Code' : 'ZIP'
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            id="country"
                                            disabled={dateReadOnly}
                                            value={country.value}
                                            error={country.error}
                                            helperText={country.message}
                                            onChange={handleFormChange}
                                            SelectProps={{ native: true }}
                                            label="Country"
                                        >
                                            <option value="Canada">Canada</option>
                                            <option value="United States">United States</option>
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>{entityLabel} Date &amp; Time</Header>
                            <Typography>
                                Enter the date and time of your event. This appears on the ticket
                                purchase page.
                            </Typography>
                            <Content>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <DatePicker
                                            id="startDate"
                                            disabled={dateReadOnly}
                                            value={startDate.value}
                                            error={startDate.error || rangeError}
                                            helperText={startDate.message}
                                            onChange={(value) =>
                                                handleDateChange('startDate', value)
                                            }
                                            label="Start Date"
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TimePicker
                                            id="startTime"
                                            disabled={dateReadOnly}
                                            value={startTime.value}
                                            error={startTime.error || rangeError}
                                            helperText={startTime.message}
                                            onChange={(value) =>
                                                handleDateChange('startTime', value)
                                            }
                                            label="Start Time"
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <DatePicker
                                            id="endDate"
                                            disabled={dateReadOnly}
                                            value={endDate.value}
                                            error={endDate.error || rangeError}
                                            helperText={endDate.message}
                                            onChange={(value) => handleDateChange('endDate', value)}
                                            label="End Date"
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TimePicker
                                            id="endTime"
                                            disabled={dateReadOnly}
                                            value={endTime.value}
                                            error={endTime.error || rangeError}
                                            helperText={endTime.message}
                                            onChange={(value) => handleDateChange('endTime', value)}
                                            label="End Time"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            id="timezone"
                                            disabled={dateReadOnly}
                                            value={timezone.value}
                                            error={timezone.error}
                                            helperText={timezone.message}
                                            onChange={handleFormChange}
                                            select
                                            InputLabelProps={{ shrink: true }}
                                            SelectProps={{ native: true }}
                                            label="Timezone"
                                        >
                                            {timezone.value === '' && <option value=""></option>}
                                            {timezoneOptions.map((tz) => (
                                                <option key={tz.name} value={tz.name}>
                                                    {tz.fullName}
                                                </option>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>
                                <DateSummary
                                    rangeError={rangeError}
                                    startDate={startDate.value}
                                    startTime={startTime.value}
                                    endDate={endDate.value}
                                    endTime={endTime.value}
                                />
                            </Content>
                        </Section>
                    </Grid>

                    {hostSeatsEnabled && !seasonLinked && (
                        <Grid item xs={12}>
                            <Section>
                                <Header>Assigned Seating</Header>
                                <Typography>
                                    Assigned seating allows ticket buyers to select specific seats,
                                    tables, or sites during checkout. This section lets you choose
                                    from seating layouts that have already been created for you.
                                </Typography>
                                <Content>
                                    {disableSeatingChart && (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            {defaultValues?.seatingChartKey && childCount > 0
                                                ? "This season's seating layout cannot change while seated events are linked."
                                                : 'Assigned seating cannot change after this season has order history.'}
                                        </Alert>
                                    )}
                                    {editMode &&
                                        parentMode &&
                                        !defaultValues?.seatingChartKey &&
                                        !disableSeatingChart && (
                                            <Typography sx={{ mb: 2 }}>
                                                You can enable seats for this season and its linked
                                                events before any tickets have been issued.
                                                Unpublish the season and its events first. Existing
                                                admission prices will be kept; assign their seating
                                                categories in Tickets after saving.
                                            </Typography>
                                        )}
                                    <Box width="100%" mb={2}>
                                        <FormControlLabel
                                            disabled={readOnly || disableSeatingChart}
                                            control={
                                                <Switch
                                                    id="assignedSeating"
                                                    checked={assignedSeatingEnabled}
                                                    onChange={handleAssignedSeatingToggle}
                                                />
                                            }
                                            label={`Use assigned seating for this ${entityLabel.toLowerCase()}`}
                                        />
                                    </Box>
                                    {assignedSeatingEnabled && (
                                        <>
                                            <TextField
                                                id="seatingChartKey"
                                                label="Select Seating Layout"
                                                select
                                                value={seatingChartKey.value}
                                                disabled={
                                                    !hasSeatingCharts ||
                                                    readOnly ||
                                                    disableSeatingChart
                                                }
                                                error={seatingChartKey.error}
                                                helperText={seatingChartKey.message}
                                                onChange={handleFormChange}
                                                SelectProps={{ native: true }}
                                                InputLabelProps={{ shrink: true }}
                                            >
                                                <option value=""></option>
                                                {seatingCharts.map((chart) => (
                                                    <option
                                                        key={chart.key}
                                                        value={String(chart.key)}
                                                    >
                                                        {chart.name || `Chart ${chart.key}`}
                                                    </option>
                                                ))}
                                            </TextField>
                                            <SeatingChartPlaceholder>
                                                {seatingChartThumbnail ? (
                                                    <Image src={seatingChartThumbnail} />
                                                ) : null}
                                            </SeatingChartPlaceholder>
                                        </>
                                    )}
                                </Content>
                            </Section>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Section>
                            <Header>{entityLabel} Image</Header>
                            <Typography>
                                Upload an image for your event. This image appears on the ticket
                                purchase page and helps attract buyers.
                            </Typography>
                            <Content mb={2}>
                                <ImagePicker
                                    disabled={readOnly}
                                    value={image}
                                    onChange={handleImageChange}
                                />
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>Taxes (Optional)</Header>
                            <Typography>
                                Turn this on only if you are required to charge tax. If taxes are
                                already included in your ticket price, leave this off.
                            </Typography>
                            <Content>
                                <Box width="100%" mb={2}>
                                    <FormControlLabel
                                        disabled={readOnly}
                                        control={
                                            <Switch
                                                id="collectTax"
                                                checked={collectTax}
                                                onChange={handleSwitchChange}
                                            />
                                        }
                                        label="Online orders (website sales)"
                                    />
                                </Box>
                                {collectTax && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box mr={[0, '1rem']}>
                                                <TextField
                                                    id="taxName"
                                                    label={
                                                        country.value === 'Canada'
                                                            ? 'Tax Name e.g. HST'
                                                            : 'Tax Name'
                                                    }
                                                    placeholder={
                                                        country.value === 'Canada'
                                                            ? 'Ex. HST, GST, QST'
                                                            : undefined
                                                    }
                                                    value={collectTax ? values.taxName.value : ''}
                                                    error={values.taxName.error && collectTax}
                                                    helperText={taxName.message}
                                                    disabled={!collectTax || readOnly}
                                                    onChange={handleFormChange}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box ml={[0, '1rem']}>
                                                <TextField
                                                    id="taxRate"
                                                    label="Tax Rate"
                                                    value={collectTax ? values.taxRate.value : ''}
                                                    error={values.taxRate.error && collectTax}
                                                    helperText={taxRate.message}
                                                    disabled={!collectTax || readOnly}
                                                    onChange={handlePercentChange}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                %
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                )}

                                <Box width="100%" mb={2} mt={2}>
                                    <FormControlLabel
                                        disabled={readOnly}
                                        control={
                                            <Switch
                                                id="collectPosTax"
                                                checked={collectPosTax}
                                                onChange={handleSwitchChange}
                                            />
                                        }
                                        label="In person orders (gate or box office)"
                                    />
                                </Box>
                                {collectPosTax && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box mr={[0, '1rem']}>
                                                <TextField
                                                    id="posTaxRate"
                                                    label="POS Tax Rate"
                                                    value={
                                                        collectPosTax ? values.posTaxRate.value : ''
                                                    }
                                                    error={values.posTaxRate.error && collectPosTax}
                                                    helperText={posTaxRate.message}
                                                    disabled={!collectPosTax || readOnly}
                                                    onChange={handlePercentChange}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                %
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                )}
                            </Content>
                        </Section>
                    </Grid>
                </Grid>

                <Box mt={4} mb={4}>
                    {defaultValues.createdAtMsg && (
                        <Typography display="block" align="right" variant="caption">
                            {defaultValues.createdAtMsg}
                        </Typography>
                    )}

                    {defaultValues.updatedAtMsg && (
                        <Typography display="block" align="right" variant="caption">
                            {defaultValues.updatedAtMsg}
                        </Typography>
                    )}
                    {editMode !== true && (
                        <Box display="flex" justifyContent="flex-end">
                            <CancelButton onClick={onCancel} disabled={loading || readOnly}>
                                Cancel
                            </CancelButton>
                            <SubmitButton
                                onClick={showTaxWarning ? handleSubmitWithTaxWarning : handleSubmit}
                                disabled={loading || readOnly}
                            >
                                {loading ? (
                                    <CircularProgress color="secondary" size={18} />
                                ) : (
                                    'Save'
                                )}
                            </SubmitButton>
                        </Box>
                    )}
                    {editMode === true && (
                        <FormSubmitBar
                            disabled={readOnly}
                            unsavedChanges={unsavedChanges}
                            loading={loading}
                            onCancel={handleFormReset}
                            onSubmit={showTaxWarning ? handleSubmitWithTaxWarning : handleSubmit}
                        />
                    )}
                </Box>
            </FormContainer>
        </Box>
    );
}

function DateSummary(props) {
    const { rangeError, startDate, startTime, endDate, endTime } = props;

    if (
        moment(startDate).isValid() === false ||
        moment(endDate).isValid() === false ||
        moment(startTime).isValid() === false ||
        moment(endTime).isValid() === false
    ) {
        return null;
    }

    const start = getMomentFromDateAndTime(startDate, startTime);
    const end = getMomentFromDateAndTime(endDate, endTime);
    const text = `${start.format('dddd, MMM Do @ h:mma')} -to- ${end.format('dddd, MMM Do @ h:mma')}`;

    if (rangeError) {
        return <DateSummaryTextError>End date cannot come before start date</DateSummaryTextError>;
    }

    if (start > end) {
        return <DateSummaryTextError>{text}</DateSummaryTextError>;
    } else {
        return <DateSummaryText>{text}</DateSummaryText>;
    }
}

const FormContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    marginTop: 4,
}));

const SeatingChartPlaceholder = styled(Box)(({ theme }) => ({
    border: '1px dashed',
    borderColor: theme.palette.grey[400],
    borderRadius: 8,
    height: 400,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.grey[600],
    marginTop: theme.spacing(2),
}));

const DateSummaryText = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    color: theme.palette.info.dark,
    marginTop: theme.spacing(2),
}));

const DateSummaryTextError = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    color: theme.palette.error.main,
    marginTop: theme.spacing(2),
}));

const Bold = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    display: 'inline',
}));

const Image = styled('img')({
    height: '100%',
    width: '100%',
    objectFit: 'contain',
});
